import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Form,
  FormActions,
  FormSection,
  GridFrame,
  GridScroll,
  LoadingState,
  SelectField,
  StatusChip,
  Table,
  Td,
  TextField,
  Th,
  Tr,
} from '@tessma/design-system';
import { Refusal, refusalText, useAsync } from '../ui/async.js';
import type { CatalogueItem, DuplicateCandidate, PartiesGateway, Party } from './gateway.js';

/**
 * **PARTY MASTER — who a tenant does business with (FR-PTY-001, 002, 005).**
 *
 * ONE RECORD, MANY RELATIONSHIPS. An organisation a tenant buys from and sells
 * to is one party with two roles, because two records drift apart the first
 * time somebody changes an address on one of them. That is the whole of D-2,
 * and it is why this screen is not inside Finance: CRM and HR will hang their
 * own relationships off these same rows.
 *
 * THE DUPLICATE CHECK IS SHOWN BEFORE IT IS ENFORCED. Typing a name asks the
 * server who it looks like; the create call is refused with the same
 * candidates if somebody presses on regardless. Nothing here compares names:
 * "Ravenscroft Joinery Ltd" and "Ravenscroft Joinery Limited" are the same
 * organisation, and deciding that from a normalised registration number and a
 * trigram index is the server's job.
 *
 * NOTHING IS DELETED. Merging makes the loser a tombstone pointing at the
 * survivor, because documents raised against it must still resolve.
 */
export function PartiesScreen({
  gateway,
  canManage,
}: {
  readonly gateway: PartiesGateway;
  readonly canManage: boolean;
}) {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'' | 'customer' | 'supplier'>('');
  const { state, reload } = useAsync<readonly Party[]>(
    () =>
      gateway.parties({
        ...(role === '' ? {} : { role }),
        ...(search === '' ? {} : { search }),
      }),
    [gateway, role, search],
  );

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'organisation' | 'person'>('organisation');
  const [identifierKind, setIdentifierKind] = useState<'vat_number' | 'company_number'>(
    'company_number',
  );
  const [identifier, setIdentifier] = useState('');
  const [newRole, setNewRole] = useState<'customer' | 'supplier'>('customer');
  const [survivor, setSurvivor] = useState('');
  const [candidates, setCandidates] = useState<readonly DuplicateCandidate[]>([]);
  const [acknowledged, setAcknowledged] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading parties…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function lookAlike(value: string) {
    setName(value);
    setAcknowledged([]);
    if (value.trim().length < 3) {
      setCandidates([]);
      return;
    }
    try {
      setCandidates(await gateway.duplicates(value));
    } catch {
      // A failed LOOK-UP is not a failed create. The server refuses a genuine
      // duplicate on the write regardless, so a convenience that cannot reach
      // the server stays silent rather than blocking the form.
      setCandidates([]);
    }
  }

  async function register() {
    setSaving(true);
    setProblem(undefined);
    try {
      await gateway.registerParty({
        partyKind: kind,
        displayName: name,
        ...(identifier === ''
          ? {}
          : { identifiers: [{ kind: identifierKind, value: identifier }] }),
        roles: [newRole],
        ...(acknowledged.length === 0 ? {} : { notADuplicateOf: acknowledged }),
      });
      setName('');
      setIdentifier('');
      setCandidates([]);
      setAcknowledged([]);
      reload();
    } catch (error) {
      // THE SERVER'S SENTENCE, which names who this looks like. "Duplicate"
      // on its own leaves somebody with nothing to merge into.
      setProblem(refusalText(error, 'The party could not be created.'));
    } finally {
      setSaving(false);
    }
  }

  async function merge(losingPartyId: string, survivingPartyId: string) {
    setProblem(undefined);
    try {
      await gateway.merge(survivingPartyId, losingPartyId);
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The parties could not be merged.'));
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="Parties"
          subtitle="Customers, suppliers, and organisations that are both. One record, many relationships."
        />
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            reload();
          }}
        >
          <FormSection title="Find">
            <TextField
              label="Name contains"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            <SelectField
              label="Role"
              value={role}
              onChange={(event) => {
                setRole(event.target.value as '' | 'customer' | 'supplier');
              }}
              options={[
                { value: '', label: 'Any role' },
                { value: 'customer', label: 'Customer' },
                { value: 'supplier', label: 'Supplier' },
              ]}
            />
            {canManage ? (
              /*
                TWO RECORDS THAT BOTH EXIST are merged by naming the one to
                KEEP and then merging the others into it. The survivor is
                chosen first deliberately: a merge is destructive from one
                side's point of view, and picking the loser first is how the
                wrong one goes.
              */
              <SelectField
                label="Party to keep, when merging"
                value={survivor}
                onChange={(event) => {
                  setSurvivor(event.target.value);
                }}
                options={state.value.map((party) => ({
                  value: party.id,
                  label: party.displayName,
                }))}
                placeholder="Not merging"
                helper="Nothing is deleted: the other becomes a tombstone pointing here, because documents raised against it must still resolve."
              />
            ) : null}
          </FormSection>
        </Form>
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        {state.value.length === 0 ? (
          <EmptyState
            icon="users-three"
            title="No party matches"
            description="A party is created before anybody can be invoiced or paid."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Parties">
                <thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Kind</Th>
                    <Th>Status</Th>
                    {canManage ? <Th>Merge</Th> : null}
                  </Tr>
                </thead>
                <tbody>
                  {state.value.map((party) => (
                    <Tr key={party.id}>
                      <Td>{party.displayName}</Td>
                      <Td variant="secondary">{party.partyKind}</Td>
                      <Td>
                        <StatusChip tone={party.status === 'active' ? 'success' : 'neutral'}>
                          {party.status}
                        </StatusChip>
                      </Td>
                      {canManage ? (
                        <Td>
                          {survivor === '' || survivor === party.id ? null : (
                            <Button
                              variant="tertiary"
                              onClick={() => {
                                void merge(party.id, survivor);
                              }}
                            >
                              Merge into the kept party
                            </Button>
                          )}
                        </Td>
                      ) : null}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canManage ? (
        <Card>
          <CardHeader
            title="Add a party"
            subtitle="Checked against who already exists before anything is written."
          />
          {candidates.length > 0 ? (
            <Alert tone="warning" title="This may already exist">
              <div>
                {candidates.map((candidate) => (
                  <p key={candidate.id}>
                    {candidate.displayName} — {candidate.reason}.{' '}
                    {acknowledged.includes(candidate.id) ? (
                      <strong>Marked as a different organisation.</strong>
                    ) : (
                      <>
                        {/*
                          USING THE EXISTING RECORD IS THE DEFAULT OFFER, and it
                          creates nothing: it clears the form and finds the row
                          that is already there. Merging is for two records that
                          both exist; the whole point here is that the second
                          one never gets made.
                        */}
                        <Button
                          variant="tertiary"
                          onClick={() => {
                            setSearch(candidate.displayName);
                            setName('');
                            setIdentifier('');
                            setCandidates([]);
                          }}
                        >
                          Use this one instead
                        </Button>
                        <Button
                          variant="tertiary"
                          onClick={() => {
                            setAcknowledged([...acknowledged, candidate.id]);
                          }}
                        >
                          This is a different organisation
                        </Button>
                      </>
                    )}
                  </p>
                ))}
              </div>
            </Alert>
          ) : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void register();
            }}
          >
            <FormSection title="Party">
              <TextField
                label="Party name"
                value={name}
                onChange={(event) => {
                  void lookAlike(event.target.value);
                }}
                helper="Asked about as you type. The server decides who it resembles, not this screen."
                required
              />
              <SelectField
                label="Kind"
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as 'organisation' | 'person');
                }}
                options={[
                  { value: 'organisation', label: 'Organisation' },
                  { value: 'person', label: 'Person' },
                ]}
              />
              <SelectField
                label="Role"
                value={newRole}
                onChange={(event) => {
                  setNewRole(event.target.value as 'customer' | 'supplier');
                }}
                options={[
                  { value: 'customer', label: 'Customer' },
                  { value: 'supplier', label: 'Supplier' },
                ]}
                helper="More roles can be added later: the same record serves both sides."
              />
              <SelectField
                label="Identifier kind"
                value={identifierKind}
                onChange={(event) => {
                  setIdentifierKind(event.target.value as 'vat_number' | 'company_number');
                }}
                options={[
                  { value: 'company_number', label: 'Company number' },
                  { value: 'vat_number', label: 'VAT number' },
                ]}
              />
              <TextField
                label="Identifier"
                value={identifier}
                onChange={(event) => {
                  setIdentifier(event.target.value);
                }}
                helper="Spacing and case do not matter: the server normalises before it compares."
              />
            </FormSection>
            <FormActions
              submitLabel="Add the party"
              submitting={saving}
              note="A probable duplicate is refused by the server, not merely flagged here."
            />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * **THE SHARED CATALOGUE (FR-PRD-001).**
 *
 * ITS OWN SCREEN, beside parties, for the same reason: both are Platform Core
 * master data that Finance consumes rather than owns. What an item MEANS TO A
 * LEDGER — nominal account, tax code, price — is set per legal entity on the
 * Finance item-defaults screen, because a nominal account is meaningless
 * outside a chart of accounts.
 *
 * Until this existed a tenant could sell only what the seed had invented, and
 * the item-defaults screen asked for a uuid to be pasted in by hand.
 */
export function CatalogueScreen({
  gateway,
  canManage,
}: {
  readonly gateway: PartiesGateway;
  readonly canManage: boolean;
}) {
  const [search, setSearch] = useState('');
  const { state, reload } = useAsync<readonly CatalogueItem[]>(
    () => gateway.catalogueItems(search === '' ? undefined : search),
    [gateway, search],
  );

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [itemKind, setItemKind] = useState<'product' | 'service'>('service');
  const [unit, setUnit] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading the catalogue…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function add() {
    setSaving(true);
    setProblem(undefined);
    try {
      await gateway.addCatalogueItem({
        code,
        name,
        itemKind,
        ...(unit === '' ? {} : { unit }),
      });
      setCode('');
      setName('');
      setUnit('');
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The item could not be added.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="Catalogue"
          subtitle="What this tenant sells and buys. Shared: Finance codes it, and the other modules will use the same rows."
        />
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            reload();
          }}
        >
          <FormSection title="Find">
            <TextField
              label="Code or name contains"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </FormSection>
        </Form>
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        {state.value.length === 0 ? (
          <EmptyState
            icon="list"
            title="No item matches"
            description="An invoice line may still be typed freehand; an item is what makes the coding automatic."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Catalogue">
                <thead>
                  <Tr>
                    <Th>Code</Th>
                    <Th>Name</Th>
                    <Th>Kind</Th>
                    <Th>Unit</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.map((item) => (
                    <Tr key={item.id}>
                      <Td>{item.code}</Td>
                      <Td>{item.name}</Td>
                      <Td variant="secondary">{item.itemKind}</Td>
                      <Td variant="secondary">{item.unit}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canManage ? (
        <Card>
          <CardHeader title="Add an item" />
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void add();
            }}
          >
            <FormSection title="Item">
              <TextField
                label="Item code"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
                helper="Unique within the tenant. Shown on documents beside the description."
                required
              />
              <TextField
                label="Item name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                required
              />
              <SelectField
                label="Kind"
                value={itemKind}
                onChange={(event) => {
                  setItemKind(event.target.value as 'product' | 'service');
                }}
                options={[
                  { value: 'service', label: 'Service' },
                  { value: 'product', label: 'Product' },
                ]}
              />
              <TextField
                label="Unit"
                value={unit}
                onChange={(event) => {
                  setUnit(event.target.value);
                }}
                helper="Day, hour, each. Left blank it is “each”."
              />
            </FormSection>
            <FormActions
              submitLabel="Add the item"
              submitting={saving}
              note="Coding it to a nominal account happens per legal entity, on the Finance item-defaults screen."
            />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}
